import type { LotI, NFTI } from '@tacans/shared-marketplace-layer';
import dayjs from 'dayjs';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import React from 'react';
import { LotStatusLabel } from 'src/features/common/collectible/components/lot-status-label';
import NftCardAuctionTime from 'src/features/common/collectible/components/nft-card-auction-time';
import { ApproveBidAction } from 'src/features/common/collectible/containers/nft-actions/approve-bid-action';
import { BuyNowAction } from 'src/features/common/collectible/containers/nft-actions/buy-now-action';
import { NftPauseAction } from 'src/features/common/collectible/containers/nft-actions/nft-pause-action';
import { PlaceBidAction } from 'src/features/common/collectible/containers/nft-actions/nft-place-bid-action';
import { useCurrentBids } from 'src/features/common/collectible/hooks/collectible.hooks';
import { useAvailableCopies } from 'src/features/common/collectible/hooks/use-available-copies';
import { useLotAvailableActions } from 'src/features/common/collectible/hooks/use-lot-available-actions';
import Currency from 'src/features/common/currency/components/currency/currency';
import { useProfileLoginUrl } from 'src/features/common/profile/hooks/use-profile-login-url';
import UserAvatar from 'src/features/common/user/components/user-avatar';
import { useDisplayName } from 'src/features/common/user/hooks/use-display-name.hooks';
import { useUserListEntities } from 'src/features/common/user/hooks/user-list.hooks';
import { useMyWalletsById } from 'src/features/common/wallet/hooks/use-my-wallets-by-id';
import { FlexBox } from 'src/ui/common/atoms/flex-box';
import { Label } from 'src/ui/common/atoms/label';
import { Table } from 'src/ui/common/molecules/table';
import TooltipComponent from 'src/ui/common/organisms/tooltip/tooltip.component';
import { decodeHTMLEntities, wipeHtml } from 'src/utils/html.utils';
import { getAuctionStatus, getLotType } from 'src/utils/lot.utils';
import { ChangePriceAction } from '../../containers/nft-actions/change-price-action';
import { ViewBidsAction } from '../../containers/nft-actions/view-bids.action';
import { StyledButtonsWrapper, StyledName, StyledRow } from './styles';

export const NftLotRow: React.VFC<{ nft: NFTI; lot: LotI }> = ({ nft, lot }) => {
    const { t } = useTranslation('pages');
    const users = useUserListEntities();
    const user = users[lot.owner];
    const userName = useDisplayName(user);
    const userUrl = useProfileLoginUrl(user);
    const { isAuction } = getLotType(lot.type);
    const currentBids = useCurrentBids();
    const actions = useLotAvailableActions({ nft, lot });
    const currentNanoBid = currentBids[lot?.id];
    const { isAuctionFinished } = getAuctionStatus(lot.auctionStartAt, lot.auctionEndAt);
    const walletsById = useMyWalletsById();
    const isYourOwn = !!walletsById[lot.owner];
    const availableCopies = useAvailableCopies();

    const priceValue = isAuction
        ? Number(currentNanoBid) || +(lot.copies ?? 0) * Number(lot.minNanoBid) + Number(lot.minNanoBidStep)
        : Number(lot.nanoPrice);

    const isNotEnoughCopiesToSell = availableCopies < +lot.copies!;

    const notEnoughCopiesToSellText = t('nft.view.lots.notEnoughCopiesToSell', {
        ns: 'pages',
        copies: availableCopies,
    });

    return (
        <StyledRow>
            <Table.Col>
                <Link href={userUrl} prefetch={false}>
                    <a>
                        <FlexBox alignItems="center">
                            <UserAvatar user={user!} variant="square" size={30} borderRadius="50" />
                            <StyledName size="sm" altTitle={decodeHTMLEntities(wipeHtml(userName))}>
                                {decodeHTMLEntities(wipeHtml(userName))}
                            </StyledName>
                            {isYourOwn && <Label>Your Own</Label>}
                        </FlexBox>
                    </a>
                </Link>
            </Table.Col>
            <Table.Col>
                <LotStatusLabel lot={lot} />
            </Table.Col>
            <Table.Col>
                {isAuction && isAuctionFinished && dayjs(lot.auctionEndAt).format('D/MM/YY, HH:mm')}
                {isAuction && !isAuctionFinished && (
                    <NftCardAuctionTime type="Table" start={lot.auctionStartAt!} end={lot.auctionEndAt!} />
                )}
            </Table.Col>
            <Table.Col>{lot.remainCopies}</Table.Col>
            <Table.Col>
                <Currency nanoPrice={priceValue!} blockchainId={nft.blockchainId} />
            </Table.Col>
            <Table.Col>
                <StyledButtonsWrapper>
                    {actions.approve && <ApproveBidAction small lot={lot} nft={nft} />}
                    {actions.placeBid && <PlaceBidAction small lot={lot} nft={nft} />}
                    {actions.buyNow && <BuyNowAction small lot={lot} nft={nft} />}
                    {actions.changePrice && (
                        <ChangePriceAction small nft={nft} lot={lot} type="CHANGE_PRICE" variant="gradient" />
                    )}
                    {actions.pause && (
                        <NftPauseAction
                            small
                            lot={lot}
                            nft={nft}
                            variant={actions.changePrice ? 'outline' : 'gradient'}
                        />
                    )}
                    {actions.viewBids && <ViewBidsAction small lot={lot} nft={nft} variant="outline" />}
                    {actions.sell && (
                        <TooltipComponent
                            position="top"
                            content={notEnoughCopiesToSellText}
                            disabled={!isNotEnoughCopiesToSell}
                        >
                            <div>
                                <ChangePriceAction small nft={nft} lot={lot} type="SELL" />
                            </div>
                        </TooltipComponent>
                    )}
                </StyledButtonsWrapper>
            </Table.Col>
        </StyledRow>
    );
};
